import React from "react";

// The itemised part of a payslip: every non-taxable allowance and every deduction on its own named line.
// The server builds it (backend services/payslip_detail.py), so the screen, the printout and the PDF cannot disagree.
// Where a payslip cannot be itemised the lump is shown with the footnote the server sends.

const php = (n) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(n || 0));

function Row({ label, value, kind = "line", testid }) {
  if (kind === "head") return <div className="pt-3 pb-1 text-xs font-bold uppercase tracking-wide text-slate-500" data-testid={testid}>{label}</div>;
  if (kind === "note") return <div className="pb-1 text-xs italic text-slate-500" data-testid={testid}>{label}</div>;
  const sub = kind === "sub";
  return (
    <div className={`flex items-start justify-between gap-3 py-2 ${sub ? "border-t font-bold" : ""}`} data-testid={testid}>
      <span className="min-w-0 break-words">{label}</span>
      <span className="shrink-0 tabular-nums">{php(value)}</span>
    </div>
  );
}

const nameOf = (l) => `${l.name}${l.category ? ` (${l.category})` : ""}`;

// Non-taxable allowances (with their own total), taxable allowances kept apart, and what is left of "other earnings".
export function AllowanceDetail({ detail }) {
  const A = detail?.allowances;
  if (!A) return null;
  const OE = detail.other_earnings || {};
  return (
    <div data-testid="allowance-detail" data-source={A.source}>
      {A.source === "lump" ? (
        <>
          {Number(A.stored_nontaxable) ? <Row label="Allowances (non-taxable)" value={A.stored_nontaxable} testid="allow-lump-nt" /> : null}
          {Number(A.stored_taxable) ? <Row label="Allowances (taxable)" value={A.stored_taxable} testid="allow-lump-t" /> : null}
          <Row kind="note" label={A.note} testid="allow-note" />
        </>
      ) : (
        <>
          {(A.nontaxable || []).length > 0 && (
            <>
              <Row kind="head" label="Non-taxable allowances" />
              {A.nontaxable.map((l, i) => <Row key={`n${i}`} label={nameOf(l)} value={l.amount} testid={`allow-nt-${i}`} />)}
              <Row kind="sub" label="Total non-taxable allowances" value={A.nontaxable_total} testid="allow-nt-total" />
            </>
          )}
          {(A.taxable || []).length > 0 && (
            <>
              <Row kind="head" label="Taxable allowances (no tax is withheld on this payslip)" />
              {A.taxable.map((l, i) => <Row key={`t${i}`} label={nameOf(l)} value={l.amount} testid={`allow-t-${i}`} />)}
              <Row kind="sub" label="Total taxable allowances" value={A.taxable_total} testid="allow-t-total" />
            </>
          )}
          {A.note ? <Row kind="note" label={A.note} testid="allow-note" /> : null}
        </>
      )}
      {OE.source === "itemised"
        ? (OE.lines || []).map((l, i) => <Row key={`o${i}`} label={`Other earnings: ${l.label}`} value={l.amount} testid={`other-earn-${i}`} />)
        : (Number(OE.total) ? <><Row label="Other Earnings" value={OE.total} /><Row kind="note" label={OE.note} /></> : null)}
    </div>
  );
}

// Every deduction by name, loans with their instalment and balance, and what was already taken off Basic Pay.
export function DeductionDetail({ detail }) {
  const D = detail?.deductions;
  if (!D) return null;
  return (
    <div data-testid="deduction-detail" data-source={D.source}>
      {(D.lines || []).map((l, i) => {
        const lo = l.loan || {};
        return (
          <React.Fragment key={i}>
            <Row label={l.label} value={l.amount} testid={`ded-${l.key}-${i}`} />
            {l.kind === "loan" && lo.instalment_no ? (
              <Row kind="note" label={`Instalment ${lo.instalment_no} of ${lo.instalments_total}, balance ${php(lo.balance_after)}`} testid={`ded-loan-note-${i}`} />
            ) : null}
          </React.Fragment>
        );
      })}
      {D.note ? <Row kind="note" label={D.note} testid="ded-note" /> : null}
      {(D.memo || []).length > 0 && (
        <>
          <Row kind="head" label="Already taken off Basic Pay (not part of Total Deductions)" />
          {D.memo.map((m, i) => <Row key={`m${i}`} label={m.label} value={m.amount} testid={`ded-memo-${m.key}`} />)}
        </>
      )}
    </div>
  );
}

// Both, for the payroll-side dialog.
export default function PayslipDetail({ detail }) {
  if (!detail) return null;
  return (
    <div className="space-y-4 text-sm" data-testid="payslip-detail">
      <div><div className="bg-slate-100 px-3 py-2 font-black">ALLOWANCES</div><AllowanceDetail detail={detail} /></div>
      <div><div className="bg-slate-100 px-3 py-2 font-black">DEDUCTIONS</div><DeductionDetail detail={detail} />
        <Row kind="sub" label="Total deductions" value={detail.deductions?.stored_total} testid="ded-total" /></div>
    </div>
  );
}
